import React, { useState, useEffect } from 'react';
import { Upload, FileSpreadsheet, FileText, CheckCircle, Info } from 'lucide-react';
import './BulkUploadManagement.css';

const BulkUploadManagement = () => {
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [systems, setSystems] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'done'
  const [showInstructions, setShowInstructions] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [teamsRes, rolesRes, systemsRes] = await Promise.all([
        fetch('/api/teams'),
        fetch('/api/roles'),
        fetch('/api/systems')
      ]);

      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (systemsRes.ok) setSystems(await systemsRes.json());
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  // Generate CSV content for role assignments
  const generateRoleAssignmentCSV = () => {
    const headers = [
      'Resource',
      'TeamID',
      'TeamName',
      'RoleID',
      'RoleName',
      'SystemID',
      'SystemName',
      'Template',
      'FunctionalRole',
      'NewAD_Group',
      'ExpectedAccess',
      'Dependencies',
      'Comments'
    ];

    // Add sample rows with AUTO for IDs
    const sampleRows = [
      [
        'Database Read Access',
        'AUTO',
        teams[0]?.Title || 'Engineering',
        'AUTO',
        roles[0]?.Title || 'Software Engineer',
        'AUTO',
        systems[0]?.Title || 'SAP',
        'Standard Access Request',
        'Developer',
        'true',
        'Day 1',
        'VPN Access Required',
        'New hire needs database access for development'
      ],
      [
        'API Gateway Access',
        'AUTO',
        teams[1]?.Title || 'Operations',
        'AUTO',
        roles[1]?.Title || 'Operations Manager',
        'AUTO',
        systems[1]?.Title || 'AWS',
        'Manager Access Request',
        '',
        'false',
        'Week 1',
        '',
        'Production environment access'
      ]
    ];

    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => 
        row.map(cell => {
          // Escape commas and quotes in cell content
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    return csvContent;
  };

  // Generate CSV content for resources
  const generateResourceCSV = () => {
    const headers = [
      'TargetSystem',
      'AccessEnvironment',
      'AccessResource',
      'DataRisk',
      'ResourceTeamManager'
    ];

    const sampleRows = [
      ['SAP', 'Prod', 'Financial Data Access', 'High', 'John Smith'],
      ['Salesforce', 'Prod', 'Customer Records', 'Critical', 'Jane Doe'],
      ['AWS', 'Dev', 'S3 Bucket Access', 'Medium', 'Bob Johnson'],
      ['Azure', 'QA', 'Database Read Access', 'Low', 'Alice Williams']
    ];

    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => 
        row.map(cell => {
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    return csvContent;
  };

  // Generate reference data CSV
  const generateReferenceDataCSV = () => {
    const sections = [];

    // Teams section
    sections.push('TEAMS');
    sections.push('TeamID,TeamName');
    teams.forEach(team => {
      sections.push(`${team.TeamID},"${team.Title}"`);
    });
    sections.push('');

    // Roles section
    sections.push('ROLES');
    sections.push('RoleID,RoleName,TeamID');
    roles.forEach(role => {
      sections.push(`${role.RoleID},"${role.Title}",${role.TeamID}`);
    });
    sections.push('');

    // Systems section
    sections.push('SYSTEMS');
    sections.push('SystemID,SystemName');
    systems.forEach(system => {
      sections.push(`${system.id},"${system.Title}"`);
    });

    return sections.join('\n');
  };

  // Download CSV file
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showNotification(`${filename} downloaded successfully!`, 'success');
  };

  // Download Excel file - convert CSV to TSV for better Excel compatibility
  const downloadXLSX = (content, filename) => {
    // Convert CSV to TSV (tab-separated values) for better Excel compatibility
    const rows = content.split('\n').map(row => {
      const cells = [];
      let cell = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          cells.push(cell.replace(/^"|"$/g, '').replace(/""/g, '"'));
          cell = '';
        } else {
          cell += char;
        }
      }
      cells.push(cell.replace(/^"|"$/g, '').replace(/""/g, '"'));
      return cells.join('\t'); // Use tabs instead of commas
    });

    const tsvContent = rows.join('\n');
    
    // Use proper Excel MIME type with BOM for UTF-8
    const blob = new Blob(['\ufeff' + tsvContent], { 
      type: 'application/vnd.ms-excel;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename.replace('.xlsx', '.xls'));
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showNotification(`${filename.replace('.xlsx', '.xls')} downloaded successfully!`, 'success');
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.className = `bulk-notification ${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${type === 'success' ? '✓' : '⚠'}</span>
        <span class="notification-message">${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      showNotification('Please select a valid CSV or Excel file', 'warning');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    await performUpload(file);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Read the first line of the selected file to detect which template type it is.
   * Returns 'resources', 'role-assignments', or null if unknown.
   */
  const detectTemplateType = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result.replace(/^\uFEFF/, ''); // strip BOM
        const firstLine = text.split('\n')[0] || '';
        // Determine delimiter: more tabs than commas → TSV
        const tabCount = (firstLine.match(/\t/g) || []).length;
        const commaCount = (firstLine.match(/,/g) || []).length;
        const headers = firstLine
          .split(tabCount > commaCount ? '\t' : ',')
          .map(h => h.trim().replace(/^"|"$/g, ''));

        if (headers.includes('TargetSystem') && headers.includes('AccessResource')) {
          resolve('resources');
        } else if (headers.includes('Resource') && headers.includes('TeamName')) {
          resolve('role-assignments');
        } else {
          resolve(null);
        }
      };
      reader.onerror = () => resolve(null);
      // Only read the first 1 KB to get headers quickly
      reader.readAsText(file.slice(0, 1024));
    });
  };

  /**
   * Generate and trigger download of an error CSV from the failed rows array.
   */
  const downloadErrorCSV = (errorRows, baseFilename) => {
    if (!errorRows || errorRows.length === 0) return;
    const headers = Object.keys(errorRows[0]);
    const escape = (val) => {
      const s = String(val == null ? '' : val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [
      headers.join(','),
      ...errorRows.map(r => headers.map(h => escape(r[h])).join(','))
    ];
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${baseFilename}_errors.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const failUpload = (message) => {
    showNotification(message, 'warning');
    setUploadStatus('error');
    setTimeout(() => {
      setUploadStatus(null);
      setSelectedFile(null);
    }, 2000);
  };

  const performUpload = async (file) => {
    if (uploading) return;
    setUploading(true);
    setUploadStatus('uploading');

    try {
      const templateType = await detectTemplateType(file);

      if (!templateType) {
        failUpload('Could not detect template type. Please upload a valid Resources or Role Assignment template.');
        setUploading(false);
        return;
      }

      const endpoint = templateType === 'resources'
        ? '/api/bulk-upload/resources'
        : '/api/bulk-upload/role-assignments';

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(endpoint, { method: 'POST', body: formData });
      const result = await response.json();

      if (!response.ok) {
        failUpload(`Upload failed: ${result.error || 'Unknown error'}`);
        setUploading(false);
        return;
      }

      const { uploaded, failed, errorRows } = result;
      const typeLabel = templateType === 'resources' ? 'resource(s)' : 'role assignment(s)';
      const message = failed === 0
        ? `Successfully uploaded ${uploaded} ${typeLabel}.`
        : `Uploaded ${uploaded} ${typeLabel}. ${failed} failed.`;

      showNotification(message, failed > 0 ? 'warning' : 'success');

      if (failed > 0 && errorRows && errorRows.length > 0) {
        const baseName = file.name.replace(/\.[^.]+$/, '');
        downloadErrorCSV(errorRows, baseName);
        setTimeout(() => {
          showNotification(`${failed} failed row(s) saved to "${baseName}_errors.csv".`, 'warning');
        }, 600);
      }

      setUploadStatus('done');
      setTimeout(() => {
        setUploadStatus(null);
        setSelectedFile(null);
      }, 2000);
    } catch (err) {
      console.error('Upload error:', err);
      failUpload('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bulk-upload-management">
      <div className="bulk-upload-header">
        <div>
          <h2>Bulk Upload Templates</h2>
          <p>Download templates for bulk importing role assignments and resources</p>
        </div>
      </div>

      {/* Information Banner */}
      <div className="info-toggle-bar">
        <button
          className="info-toggle-btn"
          onClick={() => setShowInstructions(prev => !prev)}
          title={showInstructions ? 'Hide instructions' : 'Show instructions'}
          aria-expanded={showInstructions}
        >
          <Info size={14} />
        </button>
        <span className="info-toggle-label">How to use bulk upload</span>
        {showInstructions && (
          <div className="info-panel">
            <ol>
              <li>Download the appropriate template below (CSV or Excel format)</li>
              <li>Fill in your data following the sample format — leave TeamID, RoleID, and SystemID as "AUTO"</li>
              <li>Required fields (marked *) must be completed; IDs are assigned automatically during upload</li>
              <li>Upload the completed file below — the system validates and processes it automatically</li>
            </ol>
          </div>
        )}
      </div>

      {/* Template Cards */}
      <div className="template-cards-grid">
        {/* Role Assignment Template Card */}
        <div className="template-card">
          <div className="template-card-header">
            <div className="template-icon role-icon">
              <FileSpreadsheet size={32} />
            </div>
            <div>
              <h3>Role Assignment Template</h3>
              <p>Bulk import role-based access control assignments</p>
            </div>
          </div>

          <div className="template-details">
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>Includes sample data</span>
            </div>
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>IDs pre-filled with "AUTO"</span>
            </div>
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>All required fields marked</span>
            </div>
          </div>

          <div className="template-fields">
            <strong>Included Fields:</strong>
            <div className="field-tags">
              <span className="field-tag required" title="Resource name or description">Resource</span>
              <span className="field-tag auto-fill" title="Automatically set during upload - leave as AUTO">TeamID</span>
              <span className="field-tag auto-fill" title="Automatically set during upload - leave as AUTO">RoleID</span>
              <span className="field-tag auto-fill" title="Automatically set during upload - leave as AUTO">SystemID</span>
              <span className="field-tag" title="Template type">Template</span>
              <span className="field-tag" title="Functional role designation">FunctionalRole</span>
              <span className="field-tag" title="Expected access level">ExpectedAccess</span>
              <span className="field-tag" title="Dependencies or prerequisites">Dependencies</span>
              <span className="field-tag" title="Additional comments">Comments</span>
            </div>
          </div>

          <div className="template-actions">
            <button 
              className="download-btn csv-btn"
              onClick={() => downloadCSV(generateRoleAssignmentCSV(), 'role_assignment_template.csv')}
            >
              <FileText size={16} />
              Download CSV
            </button>
            <button 
              className="download-btn xlsx-btn"
              onClick={() => downloadXLSX(generateRoleAssignmentCSV(), 'role_assignment_template.xls')}
            >
              <FileSpreadsheet size={16} />
              Download Excel
            </button>
          </div>
        </div>

        {/* Resource Template Card */}
        <div className="template-card">
          <div className="template-card-header">
            <div className="template-icon resource-icon">
              <FileSpreadsheet size={32} />
            </div>
            <div>
              <h3>Resource Template</h3>
              <p>Bulk import system resources and access permissions</p>
            </div>
          </div>

          <div className="template-details">
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>Includes sample data</span>
            </div>
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>Environment options provided</span>
            </div>
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>Risk levels documented</span>
            </div>
          </div>

          <div className="template-fields">
            <strong>Included Fields:</strong>
            <div className="field-tags">
              <span className="field-tag required">TargetSystem</span>
              <span className="field-tag required">AccessEnvironment</span>
              <span className="field-tag required">AccessResource</span>
              <span className="field-tag required">DataRisk</span>
              <span className="field-tag required">ResourceTeamManager</span>
            </div>
          </div>

          <div className="template-actions">
            <button 
              className="download-btn csv-btn"
              onClick={() => downloadCSV(generateResourceCSV(), 'resource_template.csv')}
            >
              <FileText size={16} />
              Download CSV
            </button>
            <button 
              className="download-btn xlsx-btn"
              onClick={() => downloadXLSX(generateResourceCSV(), 'resource_template.xls')}
            >
              <FileSpreadsheet size={16} />
              Download Excel
            </button>
          </div>
        </div>

        {/* Reference Data Card */}
        <div className="template-card">
          <div className="template-card-header">
            <div className="template-icon reference-icon">
              <FileText size={32} />
            </div>
            <div>
              <h3>Reference Data</h3>
              <p>Current teams, roles, and systems with IDs</p>
            </div>
          </div>

          <div className="template-details">
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>Current team listings</span>
            </div>
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>All role definitions</span>
            </div>
            <div className="detail-item">
              <CheckCircle size={16} className="check-icon" />
              <span>System identifiers</span>
            </div>
          </div>

          <div className="template-fields">
            <strong>Use this file to:</strong>
            <div className="field-tags">
              <span className="field-tag info">Lookup Team IDs</span>
              <span className="field-tag info">Find Role IDs</span>
              <span className="field-tag info">Get System IDs</span>
            </div>
          </div>

          <div className="template-actions">
            <button 
              className="download-btn csv-btn"
              onClick={() => downloadCSV(generateReferenceDataCSV(), 'reference_data.csv')}
            >
              <FileText size={16} />
              Download CSV
            </button>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <div className="upload-header">
          <Upload size={24} />
          <div>
            <h3>Bulk Upload</h3>
            <p>Upload completed templates to import data</p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {uploadStatus === 'uploading' ? (
          <div className="upload-status-area uploading">
            <FileSpreadsheet size={48} className="status-icon" />
            <h4>{selectedFile?.name}</h4>
            <p>Uploading...</p>
          </div>
        ) : uploadStatus === 'done' ? (
          <div className="upload-status-area done">
            <CheckCircle size={48} className="status-icon" />
            <h4>Upload complete</h4>
            <p>{selectedFile?.name}</p>
          </div>
        ) : uploadStatus === 'error' ? (
          <div className="upload-status-area error">
            <FileSpreadsheet size={48} className="status-icon" />
            <h4>Upload failed</h4>
            <p>{selectedFile?.name}</p>
          </div>
        ) : (
          <div
            className="upload-area"
            onClick={handleUploadAreaClick}
            title="Click to browse for a file"
          >
            <Upload size={48} className="upload-icon" />
            <h4>Upload Your Completed Template</h4>
            <p>Click to select a CSV or Excel file</p>
            <div className="upload-note">
              <Info size={16} />
              <span>Teams and roles are created automatically if they don't exist</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadManagement;
